import { Typography } from '@mui/material';
import type { Currency, UsageProductInput } from '@weft/types';
import { isEmptyArray, withDefault } from '@weft/utils';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { AddIcon } from '../../../../../../../components/icons/AddIcon';
import { useUsageProductsOrder } from '../../../../../../../hooks/use-usage-products-order';
import type { NewPlanForm } from '../../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { UsageCharges } from '../../../UsageCharges/UsageCharges';
import { AddItemMenuButton } from '../../create-plan-page-tabs-components/AddItemMenuButton';
import { EmptyTab } from '../../create-plan-page-tabs-components/EmptyTab';
import { UsageChargesMenuItems } from './usage-charges-tab-content-components/UsageChargesMenuItems';

type UsageChargesTabContentProps = {
  usageProducts: UsageProductInput[];
  currency: Currency;
};

export const UsageChargesTabContent: React.FC<UsageChargesTabContentProps> = ({ usageProducts, currency }) => {
  const { watch } = useFormContext<PlanInputForm, unknown, NewPlanForm>();

  const [usageProductsOrder, usageProductsTitles] = useUsageProductsOrder(usageProducts);
  const firstUsageProductTitle = usageProductsTitles[0];

  if (isEmptyArray(usageProducts)) {
    return (
      <EmptyTab
        title="Get Started with Usage-based fees"
        description="Define tier-based or volume-based pricing to align with your growing customer needs."
      >
        <AddItemMenuButton cta={
          <span className="flex items-center gap-x-2">
            <AddIcon fill="white" />
            <span>Set Items & Pricing</span>
          </span>
        }
        >
          <UsageChargesMenuItems />
        </AddItemMenuButton>
      </EmptyTab>
    );
  }

  const billingInterval = withDefault(watch('billingData.billingInterval'), 'Monthly');

  return (
    <div className="flex w-full flex-col gap-y-4">
      <div className="flex items-center justify-between">
        <Typography className="text-base font-medium">{firstUsageProductTitle}</Typography>

        <AddItemMenuButton
          className="bg-[#EAEEFC] text-[#458FFF]"
          cta={<span className="flex items-center gap-x-2">
            <AddIcon />
            <span>Add Item</span>
          </span>}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <UsageChargesMenuItems />
        </AddItemMenuButton>
      </div>

      <UsageCharges
        order={usageProductsOrder}
        usageProducts={usageProducts}
        editable
        billingInterval={billingInterval}
        currency={currency}
      />
    </div>
  );
};